import {
  AdditiveBlending,
  AnimationClip,
  AnimationMixer,
  Bone,
  Box3,
  Color,
  DoubleSide,
  Group,
  LoopOnce,
  Mesh,
  type MeshStandardMaterial,
  PlaneGeometry,
  Quaternion,
  ShaderMaterial,
  Vector3,
  type AnimationAction,
  type Object3D,
  type SkinnedMesh,
} from 'three';

/**
 * Aura pod stopami: miękki pierścień światła z wirującymi smugami, bez twardej krawędzi
 * (wcześniej płaski, jednolity krąg wyglądał jak naklejka).
 */
function auraMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: { color: { value: new Color() }, time: { value: 0 }, strength: { value: 0.5 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform vec3 color; uniform float time; uniform float strength;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv * 2.0 - 1.0;
        float r = length(p);
        float a = atan(p.y, p.x);
        // Pierścień z miękkimi brzegami + delikatna poświata w środku.
        float ring = exp(-pow((r - 0.72) / 0.12, 2.0));
        float inner = (1.0 - smoothstep(0.0, 0.75, r)) * 0.25;
        // Smugi krążące wokół — aura „żyje”.
        float swirl = 0.55 + 0.45 * sin(a * 3.0 - time * 1.6 + r * 6.0);
        float alpha = (ring * swirl + inner) * strength * (1.0 - smoothstep(0.92, 1.0, r));
        gl_FragColor = vec4(color * 1.6, clamp(alpha, 0.0, 1.0));
      }`,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  });
}
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { Cape, capeStyle } from './cape';
import type { Assets, ModelTemplate } from './assets';
import { decorateItem, type FxUpdater } from './rarity-fx';
import { HeroBody, type ArmorVisual, type BodyLook } from './body';
import { BODY_SOCKETS, KAYKIT_RUN_SPEED, POSE_FIXES, RACE_SOCKETS, rigFor, SOCKET_FIXES, UAL_HOLDS, UAL_READY, type PoseFix, type RigProfile, type Socket } from './rigs';

export interface EquipVisual {
  visual: string;
  color: string;
  /** 0 = zwykły … 3 = legendarny */
  tier: number;
}

/** Kości górnej połowy ciała (nazwy po sanityzacji GLTFLoadera: "hand.l" → "handl"). */
const UPPER = new Set([
  'spine', 'chest', 'head',
  'upperarml', 'lowerarml', 'wristl', 'handl', 'handslotl', 'elbowIKl', 'handIKl',
  'upperarmr', 'lowerarmr', 'wristr', 'handr', 'handslotr', 'elbowIKr', 'handIKr',
]);

type Part = 'full' | 'upper' | 'lower' | 'right' | 'left' | 'torso' | 'armR';

interface PoseState {
  fix: PoseFix;
  upper: Object3D[];
  hands: Object3D[];
  /** Obrót ramion z samej animacji (bez poprawki) z ostatniej klatki. */
  animated: Quaternion[];
}

const AXIS_Z = new Vector3(0, 0, 1);
const tmpQ = new Quaternion();
const tmpParent = new Quaternion();
const tmpRot = new Quaternion();

function partClip(
  clip: AnimationClip,
  part: Part,
  isUpper: (node: string) => boolean,
  isLeftArm?: (node: string) => boolean,
  isRightArm?: (node: string) => boolean,
): AnimationClip {
  if (part === 'full') return clip;
  const tracks = clip.tracks.filter((t) => {
    const node = t.name.split('.')[0]!;
    // „right”: tułów i prawa ręka, „left”: sama lewa ręka — osobna warstwa, którą cios jednoręczny zostawia w spokoju.
    if (part === 'right') return isUpper(node) && !isLeftArm?.(node);
    if (part === 'left') return isUpper(node) && !!isLeftArm?.(node);
    // „torso”: tułów bez rąk, „armR”: sama prawa ręka (broń w gotowości, gdy nogi i tułów biegną).
    if (part === 'torso') return isUpper(node) && !isLeftArm?.(node) && !isRightArm?.(node);
    if (part === 'armR') return !!isRightArm?.(node);
    return isUpper(node) === (part === 'upper');
  });
  return new AnimationClip(`${clip.name}:${part}`, clip.duration, tracks);
}

/**
 * Instancja animowanej postaci: klon szablonu, animacje i przypięty ekwipunek.
 * Tryb `layered` (bohater na wyprawie): nogi i tułów animowane osobno, więc atak nie zatrzymuje biegu.
 */
export class CharacterModel {
  readonly root = new Group();
  readonly body: Object3D;
  private readonly mixer: AnimationMixer;
  private readonly clips = new Map<string, AnimationClip>();
  private readonly actions = new Map<string, AnimationAction>();
  /** Kości modelu i gniazda sprzętu (w profilu innego szkieletu gniazdo to punkt przypięty do kości). */
  private readonly bones = new Map<string, Object3D>();
  private readonly rig: RigProfile | null;
  private readonly attachments: Object3D[] = [];
  private readonly layered: boolean;
  private fx: FxUpdater[] = [];
  /** Peleryna z materiału (zamiast sztywnego modelu płaszcza) — liczona po animacji szkieletu. */
  private cape: Cape | null = null;
  private loopName: string | null = null;
  private lower: AnimationAction | null = null;
  private upper: AnimationAction | null = null;
  /** Lewa ręka jako osobna warstwa pętli (szkielety z isLeftArm) — cios jednoręczny jej nie wygasza. */
  private left: AnimationAction | null = null;
  /** Bieżący atak obejmuje lewą rękę (zamach oburęczny) — po nim trzeba wrócić też z warstwą lewej ręki. */
  private attackBothArms = false;
  private attack: AnimationAction | null = null;
  /** Broń do walki wręcz w prawej ręce: spoczynek w postawie gotowości, w biegu prawa ręka trzyma ją w górze. */
  private ready = false;
  /** Prawa ręka w postawie gotowości (warstwa nad biegiem) albo null. */
  private arm: AnimationAction | null = null;
  /** Obrót gotowości broni z gniazda prawej dłoni (ciała ras mają go w samym gnieździe). */
  private readyRotation: [number, number, number] = UAL_READY.rotation;
  /** Hełm albo kaptur (zakrywają całą głowę) — wtedy chowamy włosy; pod kapeluszem i czapką zostają. */
  private headItem = false;
  private headArmor = false;
  private oneShot: AnimationAction | null = null;
  private aura: Mesh | null = null;
  /** Czubek broni w prawej ręce (w układzie kości) — do smugi za ostrzem. Null = brak broni. */
  private weaponTip: Vector3 | null = null;
  private weaponBase = new Vector3();
  private time = 0;
  private readonly pose: PoseState | null = null;
  /** Gołe ciało z manekina (strefy skóry i zbroi) — tylko bohater na szkielecie UAL. */
  private readonly heroBody: HeroBody | null = null;

  constructor(
    template: ModelTemplate,
    options: { stripAccessories?: boolean; layered?: boolean; pose?: PoseFix; body?: BodyLook; sockets?: Record<string, Partial<Socket>> } = {},
  ) {
    this.layered = options.layered ?? false;
    this.body = SkeletonUtils.clone(template.scene);
    const strip: Object3D[] = [];
    this.body.traverse((o) => {
      if (o instanceof Bone) this.bones.set(o.name, o);
      if ((o as SkinnedMesh).isSkinnedMesh) o.frustumCulled = false;
      if (options.stripAccessories && (o as Mesh).isMesh && !(o as SkinnedMesh).isSkinnedMesh && o.parent instanceof Bone) strip.push(o);
    });
    for (const o of strip) o.removeFromParent();
    this.root.add(this.body);

    // Inny szkielet niż KayKit: skala, gniazda sprzętu pod nazwami z gry.
    this.rig = rigFor(template.scene);
    if (this.rig) {
      this.body.scale.multiplyScalar(this.rig.scale);
      const fix = options.pose;
      const find = (names: string[]) => names.map((n) => this.bones.get(n)).filter((b): b is Bone => !!b);
      if (fix) {
        const upper = find(this.rig.arms.upper);
        this.pose = { fix, upper, hands: find(this.rig.arms.hand), animated: upper.map((b) => b.quaternion.clone()) };
      }
      for (const [name, base] of Object.entries(this.rig.sockets)) {
        const s = { ...base, ...options.sockets?.[name] };
        if (name === 'handslotr' && s.ready) this.readyRotation = s.ready;
        const bone = this.bones.get(s.bone);
        if (!bone) continue;
        const socket = new Group();
        socket.name = name;
        socket.position.set(...s.position);
        socket.rotation.set(...(s.rotation.map((d) => (d * Math.PI) / 180) as [number, number, number]));
        // Pomniejszona dłoń (PoseFix) nie może zmniejszać trzymanej broni.
        const handScale = this.pose?.hands.includes(bone as Bone) ? this.pose.fix.handScale ?? 1 : 1;
        socket.scale.setScalar(s.scale / handScale);
        bone.add(socket);
        this.bones.set(name, socket);
      }
    }

    if (options.body) this.heroBody = new HeroBody(this.body, options.body);

    this.mixer = new AnimationMixer(this.body);
    for (const clip of template.animations) this.clips.set(clip.name, clip);
    // Nazwy animacji z gry → klipy modelu (np. Running_A → Jog_Fwd_Loop).
    for (const [game, clip] of Object.entries(this.rig?.anims ?? {})) {
      const c = this.clips.get(clip);
      if (c && !this.clips.has(game)) this.clips.set(game, c);
    }
    this.mixer.addEventListener('finished', (e) => this.onFinished(e.action as AnimationAction));
  }

  private action(name: string, part: Part = 'full'): AnimationAction | null {
    const key = `${name}:${part}`;
    let a = this.actions.get(key);
    if (!a) {
      const clip = this.clips.get(name);
      if (!clip) return null;
      a = this.mixer.clipAction(partClip(clip, part, this.rig ? this.rig.isUpper : (n) => UPPER.has(n), this.rig?.isLeftArm, this.rig?.isRightArm));
      this.actions.set(key, a);
    }
    return a;
  }

  private onFinished(action: AnimationAction): void {
    if (action === this.attack) {
      this.attack = null;
      if (this.upper && this.lower) {
        this.upper.reset().play();
        this.upper.time = this.lower.time;
        action.crossFadeTo(this.upper, 0.15, false);
      }
      if (this.attackBothArms && this.left && this.lower) {
        this.left.reset().fadeIn(0.15).play();
        this.left.time = this.lower.time;
      }
      this.arm?.reset().fadeIn(0.15).play();
      this.attackBothArms = false;
    } else if (action === this.oneShot) {
      this.oneShot = null;
      // Powrót do pętli: nogi i (w trybie warstwowym) tułów.
      for (const back of [this.lower, this.layered ? this.upper : null, this.layered ? this.left : null, this.arm]) {
        if (!back) continue;
        back.reset().play();
        action.crossFadeTo(back, 0.2, false);
      }
    }
  }

  /** Prędkość stóp w biegu przy tempie 1, z proporcjami ciała (niższy krasnolud stawia krótsze kroki). */
  get runSpeed(): number {
    return (this.rig?.runSpeed ?? KAYKIT_RUN_SPEED) * (this.rig ? this.body.scale.y / this.rig.scale : 1);
  }

  /** Pętla spoczynku: z bronią do walki wręcz postawa gotowości (ostrze w górze), inaczej zwykły spoczynek. */
  get idleLoop(): string {
    return this.ready && this.has('Armed_Idle') ? 'Armed_Idle' : 'Idle';
  }

  has(name: string): boolean {
    return this.clips.has(name);
  }

  /** Długość animacji w sekundach (0, gdy brak). */
  clipDuration(name: string): number {
    return this.clips.get(name)?.duration ?? 0;
  }

  /** Włosy z paczki ciał (bez brody i brwi) przebijały hełmy i kaptury — pod nakryciem głowy ich nie pokazujemy. */
  private updateHair(): void {
    const hide = this.headItem || this.headArmor;
    this.body.traverse((o) => {
      if (/^Hair_(?!Beard)/.test(o.name)) o.visible = !hide;
    });
  }

  /** Warstwa prawej ręki w postawie gotowości (bieg z bronią) — włączana i wygaszana płynnie. */
  private setArm(on: boolean): void {
    const arm = on ? this.action('Armed_Idle', 'armR') : null;
    if (arm === this.arm) return;
    this.arm?.fadeOut(0.18);
    this.arm = arm;
    if (arm && !this.attack && !this.oneShot) arm.reset().fadeIn(0.18).play();
  }

  /** Animacja zapętlona (bieg, spoczynek). */
  setLoop(name: string, timeScale = 1): void {
    if (!this.clips.has(name)) return;
    if (name === this.loopName) {
      for (const a of [this.lower, this.upper, this.left]) if (a) a.timeScale = timeScale;
      return;
    }
    this.loopName = name;
    const lower = this.action(name, this.layered ? 'lower' : 'full')!;
    lower.timeScale = timeScale;
    const prevLower = this.lower;
    this.lower = lower;
    if (!this.oneShot) {
      lower.reset().play();
      if (prevLower && prevLower !== lower) prevLower.crossFadeTo(lower, 0.18, false);
    }
    if (!this.layered) return;
    const split = !!this.rig?.isLeftArm;
    // Bieg z bronią w gotowości: tułów z pętli, prawa ręka osobno w postawie Armed_Idle.
    const armed = split && this.ready && name !== 'Armed_Idle' && this.has('Armed_Idle') && !!this.rig?.isRightArm;
    this.setArm(armed);
    const upper = this.action(name, armed ? 'torso' : split ? 'right' : 'upper')!;
    upper.timeScale = timeScale;
    const prevUpper = this.upper;
    this.upper = upper;
    if (split) {
      const left = this.action(name, 'left')!;
      left.timeScale = timeScale;
      const prevLeft = this.left;
      this.left = left;
      if (!this.oneShot && !(this.attack && this.attackBothArms)) {
        left.reset().play();
        if (prevLeft && prevLeft !== left) prevLeft.crossFadeTo(left, 0.18, false);
      }
    }
    if (this.attack || this.oneShot) return;
    upper.reset().play();
    if (prevUpper && prevUpper !== upper) prevUpper.crossFadeTo(upper, 0.18, false);
  }

  /**
   * Atak tylko górną połową ciała (w trybie layered) — nogi dalej biegną.
   * `rightArm`: tylko tułów i prawa ręka (broń jednoręczna, tarcza/druga ręka zostaje na miejscu).
   */
  playAttack(name: string, timeScale = 1.5, rightArm = false): void {
    if (!this.layered) return this.playOnce(name, { timeScale });
    if (this.oneShot) return;
    const oneArm = rightArm && !!this.rig?.isLeftArm;
    const a = this.action(name, oneArm ? 'right' : 'upper');
    if (!a) return;
    a.setLoop(LoopOnce, 1);
    a.clampWhenFinished = true;
    a.timeScale = timeScale;
    this.arm?.fadeOut(0.1);
    const from = this.attack ?? this.upper;
    a.reset().play();
    if (from && from !== a) from.crossFadeTo(a, 0.1, false);
    // Zamach oburęczny przejmuje też lewą rękę; jednoręczny zostawia ją w pętli biegu/spoczynku.
    if (!oneArm && this.left && !this.attackBothArms) this.left.fadeOut(0.1);
    else if (oneArm && this.attackBothArms && this.left) this.left.reset().fadeIn(0.1).play();
    this.attackBothArms = !oneArm;
    this.attack = a;
  }

  /** Animacja jednorazowa całego ciała (śmierć, radość, obrót z bronią). `hold` zostawia ostatnią klatkę. */
  playOnce(name: string, options: { hold?: boolean; timeScale?: number; fade?: number } = {}): void {
    const action = this.action(name);
    if (!action) return;
    action.setLoop(LoopOnce, 1);
    action.clampWhenFinished = true;
    action.timeScale = options.timeScale ?? 1;
    for (const a of [this.lower, this.upper, this.left, this.arm, this.attack, this.oneShot]) {
      if (a && a !== action) a.fadeOut(options.fade ?? 0.12);
    }
    action.reset().fadeIn(options.fade ?? 0.12).play();
    this.attack = null;
    this.oneShot = options.hold ? null : action;
    if (options.hold) {
      this.lower = this.upper = this.left = null;
      this.loopName = null;
    }
  }

  /**
   * Losowa faza i lekko inne tempo pętli — tłum wrogów nie maszeruje w idealnym rytmie.
   * `seed` (np. id wroga) daje stały, ale różny dla każdego wynik.
   */
  randomizePhase(seed: number): void {
    const r = ((seed * 9301 + 49297) % 233280) / 233280;
    for (const a of [this.lower, this.upper, this.left]) {
      if (!a) continue;
      a.time = r * a.getClip().duration;
      a.timeScale *= 0.9 + r * 0.2;
    }
  }

  /** Czyści stan animacji (np. przy ponownym użyciu modelu z puli). */
  resetAnimations(): void {
    this.mixer.stopAllAction();
    this.loopName = null;
    this.lower = this.upper = this.left = this.attack = this.oneShot = null;
    this.attackBothArms = false;
  }

  get busy(): boolean {
    return this.oneShot !== null || this.attack !== null;
  }

  attach(boneName: string, object: Object3D): void {
    const bone = this.bones.get(boneName);
    if (!bone) return;
    bone.add(object);
    this.attachments.push(object);
  }

  /** Zakłada ekwipunek: klony dodatków, z efektem rzadkości. */
  setEquipment(assets: Assets, items: EquipVisual[]): void {
    for (const a of this.attachments) a.removeFromParent();
    this.attachments.length = 0;
    this.cape?.dispose();
    this.cape = null;
    this.fx = [];
    this.weaponTip = null;
    this.ready = false;
    for (const item of items) {
      // Zbroja na ciele nie ma modelu do przypięcia (zob. setArmor).
      if (item.visual.startsWith('armor:')) continue;
      const style = this.rig ? capeStyle(item.visual) : undefined;
      const cape = style ? Cape.create(style, item.tier, item.color, (name) => this.bones.get(name)) : null;
      if (cape) {
        this.cape = cape;
        this.root.add(cape.object);
        continue;
      }
      const acc = assets.accessory(item.visual);
      if (!acc) {
        console.warn(`Brak modelu dla ${item.visual}`);
        continue;
      }
      let object = acc.object.clone();
      // Inny chwyt niż miecz (kostur, kusza) na szkielecie UAL: broń w grupie z dodatkowym obrotem.
      const hold = this.rig && acc.bone === 'handslotr' ? UAL_HOLDS.find((h) => h.test.test(item.visual)) : undefined;
      const ready = !hold && !!this.rig && acc.bone === 'handslotr' && UAL_READY.test.test(item.visual);
      if (ready) this.ready = true;
      if (hold || ready) {
        const grip = new Group();
        grip.rotation.set(...((hold?.rotation ?? this.readyRotation).map((d) => (d * Math.PI) / 180) as [number, number, number]));
        object.position.y += hold?.slide ?? 0;
        grip.add(object);
        object = grip;
      }
      this.attach(acc.bone, object);
      if (acc.bone === 'handslotr') this.measureWeapon(object);
      const fx = decorateItem(object, item.color, item.tier);
      if (fx) this.fx.push(fx);
    }
    this.headItem = items.some((item) => assets.accessory(item.visual)?.bone === 'head' && /helm|kaptur/.test(item.visual));
    this.updateHair();
    // Zmiana broni zmienia postawę: odśwież bieżącą pętlę (spoczynek/bieg z ręką w gotowości).
    const loop = this.loopName;
    this.loopName = null;
    if (loop) this.setLoop(loop === 'Idle' || loop === 'Armed_Idle' ? this.idleLoop : loop);
  }

  /** Zbroja widoczna na ciele (tułów, nogi, dłonie, stopy). Bez gołego ciała nic nie robi. */
  setArmor(assets: Assets, armor: ArmorVisual[]): void {
    this.heroBody?.setArmor(armor.map((a) => ({ ...a, model: a.modelPath ? assets.template(a.modelPath).scene : undefined })));
    this.headArmor = armor.some((a) => a.slot === 'head');
    this.updateHair();
  }

  /**
   * Przyciemnia założone dodatki (mnożnik koloru materiału). W obozie mocne światło wypłukuje jasne tekstury KayKit,
   * a malowany bohater z AI ma ciemniejsze barwy — bez tego broń i hełm wyglądają blado obok postaci.
   */
  dimAttachments(k: number): void {
    for (const a of this.attachments) {
      a.traverse((o) => {
        const mesh = o as Mesh;
        const mat = mesh.material as MeshStandardMaterial;
        if (!mesh.isMesh || !mat?.isMeshStandardMaterial || mat.userData.dimmed) return;
        const dimmed = mat.clone();
        dimmed.color.multiplyScalar(k);
        dimmed.userData.dimmed = true;
        mesh.material = dimmed;
      });
    }
  }

  /**
   * Mierzy broń: najdłuższa oś bryły w układzie kości dłoni. Czubek = najdalszy koniec, podstawa = ⅓ od dłoni.
   * Dzięki temu smuga pasuje do każdej broni (miecz, topór, kostur) bez ręcznych ustawień.
   */
  private measureWeapon(object: Object3D): void {
    const bone = this.bones.get('handslotr');
    if (!bone) return;
    this.body.updateMatrixWorld(true);
    const box = new Box3().setFromObject(object);
    const corners = [box.min, box.max].flatMap((a) => [box.min, box.max].flatMap((b) => [box.min, box.max].map((c) => new Vector3(a.x, b.y, c.z))));
    const local = corners.map((c) => bone.worldToLocal(c.clone()));
    let best = local[0]!;
    for (const c of local) if (c.lengthSq() > best.lengthSq()) best = c;
    this.weaponTip = best.clone();
    this.weaponBase.copy(best).multiplyScalar(0.33);
  }

  /** Położenie podstawy i czubka broni w świecie (false, gdy postać nie ma broni). */
  weaponSegment(base: Vector3, tip: Vector3): boolean {
    const bone = this.bones.get('handslotr');
    if (!bone || !this.weaponTip) return false;
    base.copy(this.weaponBase).applyMatrix4(bone.matrixWorld);
    tip.copy(this.weaponTip).applyMatrix4(bone.matrixWorld);
    return true;
  }

  /** Poświata pod stopami — kolor najlepszej rzadkości, siła wg łącznej mocy ekwipunku. */
  setAura(color: string | null, strength: number): void {
    if (!color || strength <= 0) {
      this.aura?.removeFromParent();
      this.aura = null;
      return;
    }
    if (!this.aura) {
      this.aura = new Mesh(new PlaneGeometry(2.6, 2.6), auraMaterial());
      this.aura.rotation.x = -Math.PI / 2;
      this.aura.position.y = 0.03;
      this.root.add(this.aura);
    }
    const mat = this.aura.material as ShaderMaterial;
    mat.uniforms.color!.value.set(color);
    // Słabsza poświata pod stopami — mocna razem z obwódką i lampą robiła z bohatera świecącą figurkę.
    mat.uniforms.strength!.value = Math.min(0.22, 0.06 + strength * 0.4);
  }

  update(dt: number): void {
    this.time += dt;
    // Przed mikserem wracamy do pozy bez poprawki — klip bez ścieżki danej kości nie skumuluje obrotu.
    this.pose?.upper.forEach((b, i) => b.quaternion.copy(this.pose!.animated[i]!));
    this.mixer.update(dt);
    if (this.pose) this.applyPose(this.pose);
    this.cape?.update(dt);
    for (const f of this.fx) f(this.time);
    if (this.aura) {
      (this.aura.material as ShaderMaterial).uniforms.time!.value = this.time;
      this.aura.scale.setScalar(1 + 0.04 * Math.sin(this.time * 2));
    }
  }

  /** Odchyla ramiona na boki i zmniejsza dłonie — po animacji, w każdej klatce (mikser i tak nadpisuje kości). */
  private applyPose(pose: PoseState): void {
    const forward = AXIS_Z.clone().applyQuaternion(this.body.getWorldQuaternion(tmpQ));
    pose.upper.forEach((bone, i) => {
      pose.animated[i]!.copy(bone.quaternion);
      const parent = bone.parent!.getWorldQuaternion(tmpParent);
      // Obrót w układzie świata wokół osi przodu postaci: lewe ramię (+X) w jedną stronę, prawe w drugą.
      tmpRot.setFromAxisAngle(forward, ((i === 0 ? 1 : -1) * pose.fix.armSpread * Math.PI) / 180);
      const world = parent.clone().multiply(bone.quaternion).premultiply(tmpRot);
      bone.quaternion.copy(parent.invert().multiply(world));
    });
    const k = pose.fix.handScale ?? 1;
    if (k !== 1) for (const hand of pose.hands) hand.scale.setScalar(k);
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.root.removeFromParent();
  }
}

export function heroModel(assets: Assets, path: string, layered = false, body?: BodyLook): CharacterModel {
  // Ciała ras (manekin i modele z AI na UAL) mają proporcje manekina — wspólne gniazda sprzętu.
  const sockets = SOCKET_FIXES[path] ?? (body ? (body.textured ? RACE_SOCKETS : SOCKET_FIXES[BODY_SOCKETS]) : undefined);
  return new CharacterModel(assets.template(path), { stripAccessories: true, layered, pose: POSE_FIXES[path], body, sockets });
}
